// components/Interview/AddEditInterviewPage.jsx
import React, { useState, useEffect } from "react";
import { Form, Button, Card, Row, Col, Spinner } from "react-bootstrap";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { getCurrentUser } from "../../utils/auth";
import { can } from "../../utils/access";
import { PageHeader } from "../ui";

const currentStatuses = [
  "Scheduled", "Ongoing", "Offered", "Joined", "Rejected", "Withdrawn"
];

const initialState = {
  studentId: "",
  studentPhone: "",
  studentName: "",
  studentEmail: "",
  companyName: "",
  interviewerName: "",
  currentCTC: "",
  expectedCTC: "",
  hrName: "",
  hrContact: "",
  currentStatus: "Scheduled",
  notes: ""
};

const AddEditInterviewPage = () => {
  const { id } = useParams(); // if present, it's edit mode
  const location = useLocation();
  const [form, setForm] = useState(initialState);
  const [studentList, setStudentList] = useState([]);
  const [studentError, setStudentError] = useState("");
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(!!id); // show spinner only on edit
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const navigate = useNavigate();

  // Role detection
  const user = getCurrentUser();
  const isStudent = user?.usertype === 'student';
  const isStudentRoute = location.pathname.startsWith('/s/');

  // Load instructors
  useEffect(() => {
    const loadInstructorList = async () => {
      try {
        const { data } = await apiClient.get("/instructors/list");
        setInstructors(data.instructors || []);
      } catch {
        setToast({ show: true, message: "Failed to load instructors.", type: "error" });
      }
    };
    loadInstructorList();
  }, []);

  // Pre-fill student fields when logged-in user is a student
  useEffect(() => {
    if (!isStudent || id) return; // skip for non-students or edit mode
    const prefillStudent = async () => {
      try {
        const studentId = user?.studentId;
        if (!studentId) return;
        const { data } = await apiClient.get(`/students/${studentId}`);
        const stu = data.student;
        if (stu) {
          setForm(f => ({
            ...f,
            studentId: stu._id,
            studentPhone: stu.mobile || user.phone || "",
            studentName: stu.name || user.name || "",
            studentEmail: stu.email || user.email || "",
          }));
        }
      } catch {
        // Fallback to session data
        setForm(f => ({
          ...f,
          studentId: user?.studentId || "",
          studentPhone: user?.phone || "",
          studentName: user?.name || "",
          studentEmail: user?.email || "",
        }));
      }
    };
    prefillStudent();
  }, [isStudent, id, user?.studentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Opened from a student's record ("Add interview"): the student is already
  // known, so there is no mobile number to look up.
  const presetStudent = location.state?.student;
  useEffect(() => {
    if (id || isStudent || !presetStudent?._id) return;
    setForm((f) => ({
      ...f,
      studentId: presetStudent._id,
      studentPhone: presetStudent.mobile || "",
      studentName: presetStudent.name || "",
      studentEmail: presetStudent.email || "",
    }));
  }, [id, isStudent, presetStudent?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load interview details if edit
  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await apiClient.get(`/interviews/${id}`);
        const iv = data.interview;
        setForm({
          studentId: iv.studentId?._id || "",
          studentPhone: iv.studentId?.mobile || "",
          studentName: iv.studentId?.name || "",
          studentEmail: iv.studentId?.email || "",
          companyName: iv.companyName || "",
          interviewerName: iv.interviewerName || "",
          currentCTC: iv.currentCTC || "",
          expectedCTC: iv.expectedCTC || "",
          hrName: iv.hrName || "",
          hrContact: iv.hrContact || "",
          currentStatus: iv.currentStatus || "Scheduled",
          notes: iv.notes || "",
        });
      } catch {
        setToast({ show: true, message: "Failed to load interview.", type: "error" });
      }
      setLoading(false);
    })();
  }, [id]);

  // Student search by phone
  const handleMobileBlur = async () => {
    if (form.studentPhone.length === 10) {
      try {
        const { data } = await apiClient.get(`/students/search?mobile=${form.studentPhone}`);
        if (data.students?.length > 0) {
          const stu = data.students[0];
          setForm(f => ({ ...f, studentId: stu._id, studentName: stu.name, studentEmail: stu.email }));
          setStudentError("");
        } else {
          setStudentError("No student found for this mobile.");
          setForm(f => ({ ...f, studentId: "", studentName: "", studentEmail: "" }));
        }
      } catch {
        setStudentError("No student found for this mobile.");
        setForm(f => ({ ...f, studentId: "", studentName: "", studentEmail: "" }));
      }
    }
  };

  // Student search by name (autocomplete)
  const handleNameChange = async (e) => {
    const value = e.target.value;
    setForm(f => ({ ...f, studentName: value }));
    setStudentError("");
    if (value.length >= 2) {
      try {
        const { data } = await apiClient.get(`/students/search?text=${value}`);
        setStudentList(data.students || []);
      } catch {
        setStudentList([]);
      }
    } else {
      setStudentList([]);
    }
  };

  // Select student from autocomplete
  const handleStudentSelect = (stu) => {
    setForm(f => ({
      ...f,
      studentId: stu._id,
      studentPhone: stu.mobile,
      studentName: stu.name,
      studentEmail: stu.email,
    }));
    setStudentList([]);
    setStudentError("");
  };

  // Field change handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setToast(t => ({ ...t, show: false }));
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.studentId) {
      setStudentError("Please select a valid student (by name or mobile).");
      return;
    }
    setSaving(true);
    try {
      let interviewId;
      if (id) {
        // EDIT
        await apiClient.put(`/interviews/${id}`, form);
        interviewId = id;
        setToast({ show: true, message: "Interview updated!", type: "success" });
      } else {
        // ADD
        const { data } = await apiClient.post("/interviews/add", { ...form, rounds: [] });
        interviewId = data.interview._id;
        setToast({ show: true, message: "Interview created! Add rounds next.", type: "success" });
      }
      setTimeout(() => {
        // Redirect to details page for round management
        const basePath = isStudentRoute ? '/s/interviews' : '/interviews';
        navigate(`${basePath}/${interviewId}`);
      }, 1200);
    } catch (err) {
      setToast({
        show: true,
        message: `Failed to ${id ? "update" : "create"} interview.`,
        type: "error",
      });
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="add-interview-page p-4">
      <PageHeader
        title={id ? "Edit Interview" : "Add Interview"}
        backButton
        onBack={() => navigate(isStudentRoute ? '/s/interviews' : '/interviews')}
      />
      <Card className="p-4 shadow-sm mx-auto" style={{ maxWidth: 950 }}>
        <Form onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Label className="fw-semibold">Student Phone Number</Form.Label>
              <Form.Control
                type="tel"
                name="studentPhone"
                placeholder="Enter phone number"
                value={form.studentPhone}
                onChange={e => setForm(f => ({
                  ...f,
                  studentPhone: e.target.value,
                  studentId: "",
                  studentName: "",
                  studentEmail: "",
                }))}
                onBlur={handleMobileBlur}
                maxLength={10}
                pattern="[0-9]{10}"
                autoComplete="off"
                required
                disabled={!!id || isStudent}
              />
            </Col>
            <Col md={4} style={{ position: "relative" }}>
              <Form.Label className="fw-semibold">Student Name</Form.Label>
              <Form.Control
                type="text"
                name="studentName"
                placeholder="Enter student name"
                value={form.studentName}
                onChange={isStudent ? undefined : handleNameChange}
                autoComplete="off"
                required
                disabled={!!id || isStudent}
                readOnly={isStudent}
              />
              {!isStudent && studentList.length > 0 && (
                <div className="position-absolute bg-white border w-100" style={{ zIndex: "var(--z-dropdown)" }}>
                  {studentList.map(stu => (
                    <div
                      key={stu._id}
                      className="p-2 border-bottom student-autocomplete-item"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleStudentSelect(stu)}
                    >
                      <div>
                        <strong>{stu.name}</strong> <span className="text-muted small">{stu.mobile}</span>
                      </div>
                      <div className="small text-muted">{stu.email}</div>
                    </div>
                  ))}
                </div>
              )}
            </Col>
            <Col md={4}>
              <Form.Label className="fw-semibold">Student Email</Form.Label>
              <Form.Control
                type="email"
                name="studentEmail"
                placeholder="Student email"
                value={form.studentEmail}
                readOnly
                autoComplete="off"
                required
              />
            </Col>
          </Row>
          {studentError && (
            <div className="text-danger mb-2">{studentError}</div>
          )}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Label className="fw-semibold">Company Name</Form.Label>
              <Form.Control
                type="text"
                name="companyName"
                placeholder="Enter company name"
                value={form.companyName}
                onChange={handleChange}
                autoComplete="off"
                required
              />
            </Col>
            <Col md={6}>
              <Form.Label className="fw-semibold">Interviewer Name</Form.Label>
              <Form.Select
                name="interviewerName"
                value={form.interviewerName}
                onChange={handleChange}
                autoComplete="off"
                required
              >
                <option value="">Select Interviewer</option>
                {instructors.map(instructor => (
                  <option key={instructor._id} value={instructor._id}>
                    {instructor.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Label className="fw-semibold">Current CTC</Form.Label>
              <Form.Control
                name="currentCTC"
                placeholder="Enter current CTC"
                type="number"
                value={form.currentCTC}
                onChange={handleChange}
                required
              />
            </Col>
            <Col md={6}>
              <Form.Label className="fw-semibold">Expected CTC</Form.Label>
              <Form.Control
                name="expectedCTC"
                placeholder="Enter expected CTC"
                type="number"
                value={form.expectedCTC}
                onChange={handleChange}
                required
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Label className="fw-semibold">HR Name</Form.Label>
              <Form.Control
                type="text"
                name="hrName"
                placeholder="Enter HR name"
                value={form.hrName}
                onChange={handleChange}
                required
              />
            </Col>
            <Col md={6}>
              <Form.Label className="fw-semibold">HR Contact</Form.Label>
              <Form.Control
                type="text"
                name="hrContact"
                placeholder="Enter HR email/number"
                value={form.hrContact}
                onChange={handleChange}
                required
              />              
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Label className="fw-semibold">Current Status</Form.Label>
              <Form.Select
                name="currentStatus"
                value={form.currentStatus}
                onChange={handleChange}
                autoComplete="off"
                required
              >
                <option value="">Select Current Status</option>
                {currentStatuses.map(status => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
          <Row className="mb-4">
            <Col>
              <Form.Label className="fw-semibold">Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="notes"
                placeholder="Enter any notes"
                value={form.notes}
                onChange={handleChange}
                style={{ resize: "none" }}
              />
            </Col>
          </Row>
          <div className="d-flex gap-3">
            {(isStudent || can("interviews.manage")) && (
              <Button type="submit" variant="dark" className="px-4" disabled={saving}>
                {saving ? <Spinner size="sm" /> : id ? "Save Changes" : "Save Interview"}
              </Button>
            )}
            <Button variant="light" className="px-4 border" onClick={() => navigate(isStudentRoute ? '/s/interviews' : -1)}>
              Cancel
            </Button>
          </div>
        </Form>
      </Card>
      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
        position="top-end"
      />
    </div>
  );
};

export default AddEditInterviewPage;
